import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Search, MessageCircle, Mail, HelpCircle, CreditCard, Ticket, User, Shield } from 'lucide-react';

export function HelpPage() {
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      icon: Ticket,
      title: 'Compra de Ingressos',
      faqs: [
        {
          question: 'Como comprar um ingresso?',
          answer: 'Para comprar um ingresso, navegue pelos eventos disponíveis, selecione o evento desejado, escolha o tipo de ingresso e finalize o pagamento com cartão, PIX ou boleto.'
        },
        {
          question: 'Posso cancelar minha compra?',
          answer: 'O cancelamento depende da política do evento. Geralmente é possível cancelar até 48 horas antes do evento, sujeito a taxas administrativas. Para mais informações ou casos específicos, entre em contato diretamente com a produtora do evento.'
        },
        {
          question: 'Como recebo meu ingresso?',
          answer: 'Após a confirmação do pagamento, você receberá o ingresso digital por e-mail. Basta apresentar o QR Code na entrada do evento.'
        }
      ]
    },
    {
      icon: CreditCard,
      title: 'Pagamentos',
      faqs: [
        {
          question: 'Quais formas de pagamento aceitas?',
          answer: 'Aceitamos cartão de crédito e débito, PIX, Apple Pay, Google Pay, PicPay, boleto parcelado e PayPal. Pagamentos por maquininha devem ser realizados exclusivamente nos pontos de venda oficiais de cada evento.'
        },
        {
          question: 'Quando meu cartão será debitado?',
          answer: 'Para cartão de crédito, o valor é debitado imediatamente. Para débito e PIX, a confirmação é instantânea.'
        },
        {
          question: 'Posso parcelar minha compra?',
           answer: 'Sim, oferecemos parcelamento em até 12x no cartão de crédito, dependendo do valor da compra.'
        }
      ]
    },
    {
      icon: User,
      title: 'Conta e Perfil',
      faqs: [
        {
          question: 'Como criar uma conta?',
          answer: 'Clique em "Entrar" no menu superior e selecione "Criar conta". Preencha seus dados e confirme seu e-mail.'
        },
        {
          question: 'Esqueci minha senha',
          answer: 'Na tela de login, clique em "Esqueci minha senha" e siga as instruções enviadas para seu e-mail.'
        },
        {
          question: 'Como alterar meus dados?',
          answer: 'Acesse "Meu Perfil" no menu da sua conta e edite as informações desejadas.'
        }
      ]
    },
    {
      icon: Shield,
      title: 'Segurança',
      faqs: [
        {
          question: 'Meus dados estão seguros?',
          answer: 'Sim, utilizamos criptografia SSL e seguimos as melhores práticas de segurança para proteger seus dados pessoais e financeiros.'
        },
        {
          question: 'Como identificar ingressos falsos?',
          answer: 'Ingressos da Unique Ingressos possuem QR Code único e verificação em tempo real. Sempre compre através do nosso site oficial.'
        },
        {
          question: 'O que fazer se suspeitar de fraude?',
          answer: 'Entre em contato imediatamente com nosso suporte através do WhatsApp ou e-mail para que possamos investigar.'
        }
      ]
    }
  ];

  const contactOptions = [
    {
      icon: MessageCircle,
      title: 'WhatsApp',
      description: 'Resposta rápida via chat',
      action: 'Abrir WhatsApp',
      color: 'bg-green-500/10 text-green-600',
      onClick: () => window.open('https://api.whatsapp.com/send?l=pt&phone=5582996064988', '_blank')
    },
    {
      icon: Mail,
      title: 'E-mail',
      description: 'suporte@uniqueingressos.com.br',
      action: 'Enviar E-mail',
      color: 'bg-blue-500/10 text-blue-600',
      onClick: () => window.open('mailto:suporte@uniqueingressos.com.br', '_blank')
    }
  ];

  const filteredFAQs = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  return (
    <div className="min-h-screen pt-18">
      <section className="py-20 bg-gradient-to-br from-orange-primary/10 via-background to-orange-primary/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="font-display text-4xl md:text-5xl mb-4">
                Como podemos <span className="text-orange-primary">ajudar</span>?
              </h1>
              <p className="text-xl text-muted-foreground">
                Encontre respostas para suas dúvidas ou entre em contato conosco
              </p>
            </div>

            {/* Search Bar */}
            <div className="relative mb-12">
              <Input
                type="text"
                placeholder="Busque por uma dúvida..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pr-12 h-12 text-lg"
              />
              <Search size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            </div>

            {/* Contact Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {contactOptions.map((option, index) => (
                <Card key={index} className="text-center hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 ${option.color}`}>
                      <option.icon size={24} />
                    </div>
                    <CardTitle className="text-lg">{option.title}</CardTitle>
                    <CardDescription>{option.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={option.onClick}
                    >
                      {option.action}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* FAQ Section */}
            <div className="space-y-8">
              <h2 className="font-heading text-2xl text-center mb-8">
                Perguntas Frequentes
              </h2>

              {filteredFAQs.length === 0 && searchQuery && (
                <Card className="text-center py-8">
                  <CardContent>
                    <HelpCircle size={48} className="mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-heading text-xl mb-2">Nenhuma resposta encontrada</h3>
                    <p className="text-muted-foreground mb-4">
                      Não encontramos resultados para "{searchQuery}". Tente usar outras palavras-chave.
                    </p>
                    <Button variant="outline" onClick={() => setSearchQuery('')}>
                      Ver todas as perguntas
                    </Button>
                  </CardContent>
                </Card>
              )}

              {(searchQuery ? filteredFAQs : faqCategories).map((category, categoryIndex) => (
                <Card key={categoryIndex}>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <category.icon size={24} className="mr-3 text-orange-primary" />
                      {category.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full">
                      {category.faqs.map((faq, faqIndex) => (
                        <AccordionItem key={faqIndex} value={`item-${categoryIndex}-${faqIndex}`}>
                          <AccordionTrigger className="text-left">
                            {faq.question}
                          </AccordionTrigger>
                          <AccordionContent>
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Still Need Help */}
            <Card className="mt-12 text-center bg-gradient-to-r from-orange-primary/5 to-orange-primary/10">
              <CardContent className="py-8">
                <h3 className="font-heading text-xl mb-4">Ainda precisa de ajuda?</h3>
                <p className="text-muted-foreground mb-6">
                  Nossa equipe de suporte está pronta para ajudar você com qualquer dúvida.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    className="bg-orange-primary hover:bg-orange-primary/90"
                    onClick={() => window.open('https://api.whatsapp.com/send?l=pt&phone=5582996064988', '_blank')}
                  >
                    <MessageCircle size={16} className="mr-2" />
                    Falar no WhatsApp
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open('mailto:suporte@uniqueingressos.com.br', '_blank')}
                  >
                    <Mail size={16} className="mr-2" />
                    Enviar E-mail
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}